import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
} from '@chakra-ui/react'
import { ShoppingBag, User, Calendar, Truck } from '@phosphor-icons/react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface OrderProduct {
  quantity: number
  unitPrice: number
  unitCost: number
  productName: string | null
  sku: string | null
}

interface OrderData {
  id: number
  channelOrderNumber: string
  status: string
  soldTotal: number
  costTotal: number | null
  shippingPrice: number | null
  createdAt: string
  shippedAt: string | null
  deliveredAt: string | null
  canceledAt: string | null
  customerName: string | null
  customerEmail: string | null
  channelName: string | null
  products?: OrderProduct[]
}

const statusTranslations: Record<string, string> = {
  pending: 'Pendente',
  processing_payment: 'Processando Pagamento',
  paid: 'Pago',
  shipped: 'Enviado',
  delivered: 'Entregue',
  canceled: 'Cancelado',
  processing_cancel: 'Processando Cancelamento',
  waiting_payment: 'Aguardando Pagamento',
  waiting_invoices: 'NF-e Pendente',
  waiting_label: 'Etiqueta Pendente',
  wms: 'Em Separação',
}

const statusColors: Record<string, string> = {
  pending: 'yellow',
  paid: 'green',
  shipped: 'blue',
  delivered: 'green',
  canceled: 'red',
  processing_cancel: 'red',
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

export default function OrderDetailModal({
  isOpen,
  onClose,
  order,
}: {
  isOpen: boolean
  onClose: () => void
  order: OrderData
}) {
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg={surfaceBg} maxH="85vh">
        <ModalHeader borderBottom="1px" borderColor={borderColor}>
          <HStack>
            <ShoppingBag size={20} color="#ECC94B" />
            <Text color={textPrimary} fontSize="md">
              Pedido #{order.channelOrderNumber}
            </Text>
            <Badge colorScheme={statusColors[order.status] || 'gray'}>
              {statusTranslations[order.status] || order.status}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color={textMuted} />
        <ModalBody py={4}>
          <VStack spacing={4} align="stretch">
            {/* Informacoes gerais */}
            <Box bg={subtleBg} p={3} borderRadius="md">
              <Text fontSize="sm" fontWeight="semibold" color={textSecondary} mb={2}>
                Informações Gerais
              </Text>
              <VStack spacing={2} align="stretch">
                <HStack justify="space-between">
                  <HStack spacing={1}>
                    <Text fontSize="xs" color={textMuted}>ID:</Text>
                  </HStack>
                  <Text fontSize="xs" color={textPrimary}>{order.id}</Text>
                </HStack>
                {order.channelName && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color={textMuted}>Canal:</Text>
                    <Text fontSize="xs" color={textPrimary}>{order.channelName}</Text>
                  </HStack>
                )}
                <HStack justify="space-between">
                  <HStack spacing={1}>
                    <Calendar size={12} color="#A0AEC0" />
                    <Text fontSize="xs" color={textMuted}>Criado em:</Text>
                  </HStack>
                  <Text fontSize="xs" color={textPrimary}>
                    {format(new Date(order.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </Text>
                </HStack>
                {order.shippedAt && (
                  <HStack justify="space-between">
                    <HStack spacing={1}>
                      <Truck size={12} color="#A0AEC0" />
                      <Text fontSize="xs" color={textMuted}>Enviado em:</Text>
                    </HStack>
                    <Text fontSize="xs" color={textPrimary}>
                      {format(new Date(order.shippedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </Text>
                  </HStack>
                )}
                {order.deliveredAt && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color={textMuted}>Entregue em:</Text>
                    <Text fontSize="xs" color="green.400">
                      {format(new Date(order.deliveredAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </Text>
                  </HStack>
                )}
                {order.canceledAt && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color={textMuted}>Cancelado em:</Text>
                    <Text fontSize="xs" color="red.400">
                      {format(new Date(order.canceledAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Box>

            {/* Cliente */}
            {order.customerName && (
              <Box bg={subtleBg} p={3} borderRadius="md">
                <Text fontSize="sm" fontWeight="semibold" color={textSecondary} mb={2}>
                  Cliente
                </Text>
                <HStack spacing={2}>
                  <User size={14} color="#A0AEC0" />
                  <VStack spacing={0} align="start">
                    <Text fontSize="xs" color={textPrimary}>{order.customerName}</Text>
                    {order.customerEmail && (
                      <Text fontSize="xs" color={textMuted}>{order.customerEmail}</Text>
                    )}
                  </VStack>
                </HStack>
              </Box>
            )}

            {/* Valores */}
            <Box bg={subtleBg} p={3} borderRadius="md">
              <Text fontSize="sm" fontWeight="semibold" color={textSecondary} mb={2}>
                Valores
              </Text>
              <VStack spacing={2} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="xs" color={textMuted}>Total Venda:</Text>
                  <Text fontSize="sm" fontWeight="bold" color="green.400">
                    {formatCurrency(order.soldTotal)}
                  </Text>
                </HStack>
                {order.costTotal != null && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color={textMuted}>Total Custo:</Text>
                    <Text fontSize="xs" color={textPrimary}>{formatCurrency(order.costTotal)}</Text>
                  </HStack>
                )}
                {order.shippingPrice != null && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color={textMuted}>Frete:</Text>
                    <Text fontSize="xs" color={textPrimary}>{formatCurrency(order.shippingPrice)}</Text>
                  </HStack>
                )}
              </VStack>
            </Box>

            {/* Produtos */}
            {order.products && order.products.length > 0 && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color={textSecondary} mb={2}>
                  Produtos ({order.products.length})
                </Text>
                <TableContainer>
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th color={textMuted} borderColor={borderSubtle}>Produto</Th>
                        <Th color={textMuted} borderColor={borderSubtle} isNumeric>Qtd</Th>
                        <Th color={textMuted} borderColor={borderSubtle} isNumeric>Preço</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {order.products.map((prod, idx) => (
                        <Tr key={idx}>
                          <Td borderColor={borderColor} maxW="200px">
                            <Text fontSize="xs" color={textPrimary} noOfLines={2}>
                              {prod.productName || 'Produto removido'}
                            </Text>
                            {prod.sku && (
                              <Text fontSize="2xs" color={textMuted}>SKU: {prod.sku}</Text>
                            )}
                          </Td>
                          <Td borderColor={borderColor} isNumeric>
                            <Text fontSize="xs" color={textPrimary}>{prod.quantity}</Text>
                          </Td>
                          <Td borderColor={borderColor} isNumeric>
                            <Text fontSize="xs" color={textPrimary}>{formatCurrency(prod.unitPrice)}</Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
